/**
 * A CONFIGURAÇÃO DE FATURAMENTO DO CLIENTE, ORGANIZADA (16/10/2026, fase 1 do plano de empenho, aprovada pelo Heitor:
 * "Empenho e configuração: aprovo o plano" e "Ficha do cliente: com perfis").
 *
 * Os 8 interruptores da ficha estavam soltos numa grade, sem ordem. Agora vão em 4 perguntas, com uma frase que diz o
 * efeito de tudo, 3 perfis de partida e as combinações sem sentido travadas com o motivo. ⛔ NENHUMA REGRA MUDA: os
 * campos gravados e o payload são os mesmos de antes; isto é só a forma de configurar.
 */

/** Os campos do cliente que esta parte da ficha mexe (os nomes são os do backend, sem mudança). */
export type CamposDoFaturamento = {
  isPublicSector?: boolean;
  paysByFiscalDocument?: boolean;
  hideNfsePeriod?: boolean;
  splitFiscalInvoices?: boolean;
  manualFiscalEmission?: boolean;
  emailDeliveryType?: string;
  includeXmlInEmail?: boolean;
  zipFiscalAttachments?: boolean;
  approvalDeferredReason?: string | null;
  approvalDeferredDay?: number | null;
};

export const ENVIO_POR_EMAIL: { valor: string; rotulo: string }[] = [
  { valor: 'nf_fatura_boleto', rotulo: 'NF+Fatura+Boleto' },
  { valor: 'nf_fatura', rotulo: 'NF+Fatura' },
  { valor: 'nf_fatura_relatorio', rotulo: 'NF+Fatura+Relat.' },
  { valor: 'nf_fatura_boleto_relatorio', rotulo: 'NF+Fat+Bol+Relat.' },
  { valor: 'nenhum', rotulo: 'Nao enviar' },
];

/** A retenção está ligada quando há motivo (string, mesmo vazia): é o que o backend lê, e o que o interruptor sempre usou. */
export const aprovacaoAdiada = (f: CamposDoFaturamento) => f.approvalDeferredReason !== null && f.approvalDeferredReason !== undefined;
const semEmail = (f: CamposDoFaturamento) => (f.emailDeliveryType || 'nf_fatura_boleto') === 'nenhum';

/**
 * A frase do topo, com o efeito de tudo: "2 notas por mês (NFS-e e NFCom), emitidas na aprovação, pagas nota a nota;
 * enviadas por e-mail (NF+Fatura+Boleto) com XML, em ZIP; aprovação retida até o dia 15."
 */
export function resumoDoFaturamento(f: CamposDoFaturamento): string {
  const duas = !!f.splitFiscalInvoices;
  const notas = duas ? '2 notas por mês (NFS-e e NFCom)' : '1 nota por mês';
  const emitida = f.manualFiscalEmission ? (duas ? 'emitidas à mão' : 'emitida à mão') : (duas ? 'emitidas na aprovação' : 'emitida na aprovação');
  const quem = [
    f.isPublicSector ? 'órgão público (período do mês anterior)' : null,
    // O interruptor "Paga por Empenho" grava `paysByFiscalDocument`: cada nota é paga em separado, e a conciliação sugere o
    // pagamento parcial pelo valor dela. NÃO é controle de saldo de empenho, e a frase não pode dar a entender (16/10/2026).
    f.paysByFiscalDocument ? 'pago nota a nota' : null,
    f.hideNfsePeriod ? 'sem o período na nota' : null,
  ].filter(Boolean);
  const primeira = [notas, emitida, ...quem].join(', ');
  let envio: string;
  if (semEmail(f)) envio = duas ? 'não enviadas por e-mail' : 'não enviada por e-mail';
  else {
    const rotulo = ENVIO_POR_EMAIL.find((o) => o.valor === (f.emailDeliveryType || 'nf_fatura_boleto'))?.rotulo || f.emailDeliveryType;
    envio = `${duas ? 'enviadas' : 'enviada'} por e-mail (${rotulo})${f.includeXmlInEmail ? ' com XML' : ''}${f.zipFiscalAttachments ? ', em ZIP' : ''}`;
  }
  const aprovar = aprovacaoAdiada(f)
    ? (f.approvalDeferredDay ? `aprovação retida até o dia ${f.approvalDeferredDay}` : 'aprovação retida até liberar à mão')
    : null;
  return `${[primeira, envio, aprovar].filter(Boolean).join('; ')}.`;
}

/**
 * Combinação sem sentido: o interruptor trava LIGAR, com o motivo ao lado. ⚠️ Se o cliente já está com ele ligado, a
 * ficha não o desliga sozinha (seria gravar por baixo do pano): mostra o motivo e deixa desligar.
 */
export function motivoDaTrava(campo: keyof CamposDoFaturamento, f: CamposDoFaturamento): string | null {
  if ((campo === 'includeXmlInEmail' || campo === 'zipFiscalAttachments') && semEmail(f)) {
    return campo === 'zipFiscalAttachments' ? 'Sem envio por e-mail, não há o que zipar.' : 'Sem envio por e-mail, o XML não vai para ninguém.';
  }
  return null;
}

/** Os perfis de partida. Preenchem os interruptores; quem grava é o Salvar da ficha, depois de a pessoa revisar. */
export type PerfilDeFaturamento = { chave: string; rotulo: string; campos: Partial<CamposDoFaturamento> };
export const PERFIS_DE_FATURAMENTO: PerfilDeFaturamento[] = [
  { chave: 'privada', rotulo: 'Empresa privada', campos: { isPublicSector: false, paysByFiscalDocument: false, manualFiscalEmission: false } },
  { chave: 'publico', rotulo: 'Órgão público', campos: { isPublicSector: true, paysByFiscalDocument: false, manualFiscalEmission: false } },
  // FUNDETEC: uma NFS-e por empenho, nas datas em que os empenhos saem. A nota cheia não pode sair na aprovação.
  { chave: 'empenho', rotulo: 'Órgão que paga por empenho', campos: { isPublicSector: true, paysByFiscalDocument: true, manualFiscalEmission: true } },
];

const ROTULO_DO_CAMPO: Record<string, string> = {
  isPublicSector: 'Órgão público', paysByFiscalDocument: 'Paga por empenho', manualFiscalEmission: 'Emissão manual',
};
/** O que o perfil mudaria, em palavras: "Órgão público: ligado; Emissão manual: ligado". Vazio = já está assim. */
export function oQueOPerfilMuda(p: PerfilDeFaturamento, f: CamposDoFaturamento): string[] {
  return Object.entries(p.campos)
    .filter(([k, v]) => !!(f as any)[k] !== !!v)
    .map(([k, v]) => `${ROTULO_DO_CAMPO[k] || k}: ${v ? 'ligado' : 'desligado'}`);
}
